import React, { useState, useEffect } from 'react';
import { Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { getMaintenanceConfig } from '@/services/tconnect/maintenance';
import { sanitizeMaintenanceHtml, sanitizeMaintenanceTitle } from '@/utils/sanitizeHtml';
import { getIntl, getLocale } from '@umijs/max';
import moment from 'moment';

const POPUP_LAST_SHOWN_KEY = 'maintenancePopupShownAt3' + moment().format('YYYYMMDD');
const SHOW_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes
const AUTO_CLOSE_MS = 30000;

const getDefaultTitle = () =>
  getIntl(getLocale()).formatMessage({
    id: 'pages.maintenance.defaultTitle',
    defaultMessage: 'Thông báo bảo trì hệ thống',
  });

const MaintenancePopup: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [title, setTitle] = useState(getDefaultTitle);
  const [message, setMessage] = useState('');

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    const loadAndMaybeShow = async () => {
      try {
        const config = await getMaintenanceConfig();
        if (!config?.enabled || !config.message?.trim()) {
          return;
        }

        setTitle(sanitizeMaintenanceTitle(config.title || getDefaultTitle()));
        setMessage(sanitizeMaintenanceHtml(config.message));

        const lastShownAt = localStorage.getItem(POPUP_LAST_SHOWN_KEY);
        const now = Date.now();
        const shouldShowPopup = !lastShownAt || now - Number(lastShownAt) >= SHOW_INTERVAL_MS;

        if (!shouldShowPopup) {
          return;
        }

        setVisible(true);
        timer = setTimeout(() => {
          setVisible(false);
          localStorage.setItem(POPUP_LAST_SHOWN_KEY, String(Date.now()));
        }, AUTO_CLOSE_MS);
      } catch {
        // ignore — popup is optional
      }
    };

    loadAndMaybeShow();

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, []);

  const handleClose = () => {
    setVisible(false);
    localStorage.setItem(POPUP_LAST_SHOWN_KEY, String(Date.now()));
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ExclamationCircleOutlined style={{ color: '#faad14' }} />
          <span>{title}</span>
        </div>
      }
      open={visible}
      onCancel={handleClose}
      footer={null}
      closable={true}
      maskClosable={false}
      centered
      width={520}
      style={{ top: '20%' }}
    >
      <div
        style={{
          padding: '12px 0 8px',
          fontSize: '15px',
          lineHeight: '1.6',
        }}
      >
        <div className="maintenance-popup-content" dangerouslySetInnerHTML={{ __html: message }} />
        <p
          style={{
            margin: '16px 0 0 0',
            fontSize: '13px',
            color: '#666',
            fontStyle: 'italic',
            textAlign: 'center',
          }}
        >
          Thông báo này sẽ tự động đóng sau {AUTO_CLOSE_MS / 1000} giây
        </p>
      </div>
    </Modal>
  );
};

export default MaintenancePopup;
